/**
 * Where a STORY tour remembers itself: sessionStorage, and nowhere else.
 *
 * A story tour (the demo's) has no user to store progress against: every
 * visitor is a throwaway account, so the tab is the unit. sessionStorage
 * survives the full page loads a tour crosses (the lease redirect, a manual
 * refresh) and dies with the tab, which is the right lifetime. Storage can be
 * unavailable (private mode, blocked site data); every call tolerates that,
 * and the tour simply does not resume.
 */
const KEY = 'lw:tour';

export function readStory() {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    if (!saved || typeof saved.id !== 'string' || !Number.isInteger(saved.index)) return null;
    const ints = list => (Array.isArray(list) ? list.filter(Number.isInteger).slice(-50) : []);
    return {
      id: saved.id,
      index: saved.index,
      status: saved.status === 'paused' ? 'paused' : 'running',
      // The outreach campaign that opened this tour. The address only carries
      // it until the tour's first navigation, and the closing card needs it.
      campaign: typeof saved.campaign === 'string' ? saved.campaign : null,
      // Spec 33: where Back stops (a step that wrote to the server) and the
      // branch the visitor took, so a refresh does not let Back undo either.
      floor: Number.isInteger(saved.floor) ? saved.floor : 0,
      history: ints(saved.history),
    };
  } catch (e) {
    return null;
  }
}

export function writeStory({ id, index, status, campaign = null, floor = 0, history = [] }) {
  try {
    window.sessionStorage.setItem(
      KEY,
      JSON.stringify({ id, index, status, campaign, floor, history })
    );
  } catch (e) {
    // The tour works for this page and does not resume on the next. Fine.
  }
}

export function clearStory() {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch (e) {
    // Nothing to clear is the state we wanted.
  }
}

/*
 * Spec 33 section 5.6: what this tab has done across walkthroughs.
 *
 * Separate from the current tour's position (above) because it outlives any
 * one tour: the walkthrough menu ticks what is finished, "Assign a driver"
 * picks up the load "Create a load" made, and the Explore checklist remembers
 * what has been tried. Per tab, like the position, because the visitor's
 * tenant is per lease and a second tab may be a second look. Small on
 * purpose: ids and a few values, never a server response.
 */
const PROGRESS_KEY = 'lw:walkthroughs';
const CONTEXT_KEYS = ['po', 'loadId', 'customer', 'driver', 'truck'];

function emptyProgress() {
  return { done: [], explored: [], context: {} };
}

export function readProgress() {
  try {
    const raw = window.sessionStorage.getItem(PROGRESS_KEY);
    if (!raw) return emptyProgress();
    const saved = JSON.parse(raw) || {};
    const strings = list =>
      Array.isArray(list) ? list.filter(v => typeof v === 'string').slice(0, 50) : [];
    const context = {};
    for (const key of CONTEXT_KEYS) {
      const value = saved.context && saved.context[key];
      if (typeof value === 'string' || Number.isInteger(value)) context[key] = value;
    }
    return { done: strings(saved.done), explored: strings(saved.explored), context };
  } catch (e) {
    return emptyProgress();
  }
}

function writeProgress(progress) {
  try {
    window.sessionStorage.setItem(PROGRESS_KEY, JSON.stringify(progress));
  } catch (e) {
    // Progress is a convenience; without storage the menu just ticks nothing.
  }
  return progress;
}

/** Remember a value a later walkthrough needs (the load this tab created). */
export function rememberContext(values) {
  const progress = readProgress();
  for (const key of CONTEXT_KEYS) {
    if (values && Object.prototype.hasOwnProperty.call(values, key)) {
      const value = values[key];
      if (value === null || value === undefined) delete progress.context[key];
      else if (typeof value === 'string' || Number.isInteger(value)) progress.context[key] = value;
    }
  }
  return writeProgress(progress);
}

function addTo(list, id) {
  const progress = readProgress();
  if (typeof id === 'string' && !progress[list].includes(id)) progress[list].push(id);
  return writeProgress(progress);
}

export function markDone(walkthroughId) {
  return addTo('done', walkthroughId);
}

export function markExplored(itemId) {
  return addTo('explored', itemId);
}
